import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Chip from 'tio-reimagined/components/chip';
import { eq } from 'tio-ui/utilities';
import { PhWarning, PhArrowRight } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';

/**
 * SmartAction type representing a contextual action for the user
 */
export interface SmartAction {
  id: string;
  priority: 'urgent' | 'high' | 'medium' | 'low';
  category: 'money' | 'career' | 'deadline' | 'opportunity';
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  title: string;
  description: string;
  impact?: string;
  cta: string;
  route: string;
}

/**
 * SmartActionCard - Displays a contextual action card with priority-based styling
 *
 * Smart actions surface personalized recommendations based on the user's situation.
 * Priority levels affect visual treatment via the `.tio-smart-action-card--<priority>`
 * modifier which sets the left stripe, icon-badge tint, and chip-badge color:
 * - urgent  → danger  (red stripe + "Action needed" badge)
 * - high    → warning (amber stripe)
 * - medium  → info    (teal stripe — was Tailwind blue, info is the canonical
 *                      replacement for status blue per the playbook)
 * - low     → neutral (subtle border stripe)
 *
 * @example
 * ```gts
 * <SmartActionCard @item={{this.smartAction}} />
 * ```
 */
interface SmartActionCardSignature {
  Args: {
    /** The smart action data to display */
    item: SmartAction;
  };
  Element: HTMLAnchorElement;
}

const SmartActionCard: TOC<SmartActionCardSignature> = <template>
  {{! template-lint-disable no-bare-strings }}
  <LinkTo
    @route={{@item.route}}
    class="tio-smart-action-card tio-smart-action-card--{{@item.priority}}"
    role="article"
    aria-label="{{@item.title}}: {{@item.description}}"
    ...attributes
  >
    <div class="tio-row-top mb-3">
      <div class="tio-smart-action-card-icon">
        {{#let @item.icon as |ActionIcon|}}
          <ActionIcon @size={{20}} />
        {{/let}}
      </div>

      <div class="flex-1 min-w-0">
        <div class="tio-row-tight mb-1 flex-wrap">
          {{#if (eq @item.priority "urgent")}}
            <Chip class="tio-smart-action-card-badge text-xs">
              <PhWarning @size={{12}} class="mr-1" />
              Action needed
            </Chip>
          {{/if}}
          {{#if @item.impact}}
            <span class="tio-smart-action-card-impact">{{@item.impact}}</span>
          {{/if}}
        </div>
        <Heading @level={{3}}>
          {{@item.title}}
        </Heading>
      </div>
    </div>

    <Text @variant="muted" @size="sm" class="mb-4 line-clamp-2">
      {{@item.description}}
    </Text>

    <div class="tio-smart-action-card-cta" aria-hidden="true">
      <span>{{@item.cta}}</span>
      <PhArrowRight @size={{16}} />
    </div>
  </LinkTo>
</template>;

export default SmartActionCard;
